/**
 * Centralized body scroll management utility
 * Prevents conflicts when multiple components try to control body overflow
 */

let scrollLockCount = 0;
let originalBodyOverflow = '';
let originalBodyTouchAction = '';

/**
 * Lock body scrolling (prevent page scroll)
 * Uses a counter to handle multiple simultaneous locks
 */
export const lockBodyScroll = () => {
  scrollLockCount++;
  
  // Only modify if this is the first lock
  if (scrollLockCount === 1) {
    // Save original values
    originalBodyOverflow = document.body.style.overflow || '';
    originalBodyTouchAction = document.body.style.touchAction || '';
    
    // Apply lock
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
  }
};

/**
 * Unlock body scrolling (restore page scroll)
 * Uses a counter to handle multiple simultaneous locks
 */
export const unlockBodyScroll = () => {
  scrollLockCount = Math.max(0, scrollLockCount - 1);
  
  // Only restore if all locks are released
  if (scrollLockCount === 0) {
    document.body.style.overflow = originalBodyOverflow;
    document.body.style.touchAction = originalBodyTouchAction;
    
    // Clear stored values
    originalBodyOverflow = '';
    originalBodyTouchAction = '';
  }
};

/**
 * Force unlock body scrolling (emergency reset)
 * Use this if you suspect locks aren't being released properly
 */
export const forceUnlockBodyScroll = () => {
  scrollLockCount = 0;
  document.body.style.overflow = '';
  document.body.style.touchAction = '';
  originalBodyOverflow = '';
  originalBodyTouchAction = '';
};

/**
 * Scroll an element into view within its scrollable container
 * Prevents scrolling the entire page/window
 */
export const scrollIntoViewContainer = (element, container, options = {}) => {
  if (!element || !container) return;
  
  const {
    behavior = 'auto',
    block = 'center',
    inline = 'nearest'
  } = options;
  
  // Get element and container positions
  const elementRect = element.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  
  // Calculate if element is already visible in container
  const isVisible = (
    elementRect.top >= containerRect.top &&
    elementRect.bottom <= containerRect.bottom &&
    elementRect.left >= containerRect.left &&
    elementRect.right <= containerRect.right
  );
  
  if (isVisible && behavior !== 'smooth') {
    // Already visible, no need to scroll
    return;
  }
  
  // Calculate scroll position needed
  let scrollTop = container.scrollTop;
  let scrollLeft = container.scrollLeft;
  
  // Get element's position relative to container
  const elementOffsetTop = element.offsetTop - container.offsetTop;
  const elementOffsetLeft = element.offsetLeft - container.offsetLeft;
  
  // Calculate desired scroll position based on block option
  switch (block) {
    case 'start':
      scrollTop = elementOffsetTop;
      break;
    case 'end':
      scrollTop = elementOffsetTop - container.clientHeight + element.offsetHeight;
      break;
    case 'center':
      scrollTop = elementOffsetTop - (container.clientHeight / 2) + (element.offsetHeight / 2);
      break;
    case 'nearest':
      // If element is above viewport, scroll to show top
      if (elementRect.top < containerRect.top) {
        scrollTop = elementOffsetTop;
      }
      // If element is below viewport, scroll to show bottom
      else if (elementRect.bottom > containerRect.bottom) {
        scrollTop = elementOffsetTop - container.clientHeight + element.offsetHeight;
      }
      break;
  }
  
  // Calculate desired scroll position based on inline option
  switch (inline) {
    case 'start':
      scrollLeft = elementOffsetLeft;
      break;
    case 'end':
      scrollLeft = elementOffsetLeft - container.clientWidth + element.offsetWidth;
      break;
    case 'center':
      scrollLeft = elementOffsetLeft - (container.clientWidth / 2) + (element.offsetWidth / 2);
      break;
    case 'nearest':
      // If element is left of viewport, scroll to show left
      if (elementRect.left < containerRect.left) {
        scrollLeft = elementOffsetLeft;
      }
      // If element is right of viewport, scroll to show right
      else if (elementRect.right > containerRect.right) {
        scrollLeft = elementOffsetLeft - container.clientWidth + element.offsetWidth;
      }
      break;
  }
  
  // Perform scroll
  if (behavior === 'smooth') {
    container.scrollTo({
      top: scrollTop,
      left: scrollLeft,
      behavior: 'smooth'
    });
  } else {
    container.scrollTop = scrollTop;
    container.scrollLeft = scrollLeft;
  }
  
  // Prevent any window scroll that might have been triggered
  window.scrollTo(0, window.scrollY);
};

